/**
 * Diagram API
 *
 * Wraps `/api/v1/diagrams/*` and `/api/v1/sessions/{id}/diagrams` endpoints.
 * The shared axios client has baseURL `/api/v1`.
 */

import client from './client';

/**
 * List diagrams scoped to a TARA session. Accepts pagination via
 * `limit` (default 100) and `offset` (default 0). Mirrors
 * AthenaBackendService.listDiagrams({ sessionId, limit, offset }).
 */
export async function listDiagramsForSession(
  sessionId,
  { limit = 100, offset = 0 } = {}
) {
  const params = new URLSearchParams({ limit, offset });
  return client.get(
    `/sessions/${encodeURIComponent(sessionId)}/diagrams?${params.toString()}`
  );
}

export async function getDiagramsForSessionV2(sessionId) {
  return client.get(`/diagrams/session/${sessionId}`);
}

export async function getDiagram(diagramId) {
  return client.get(`/diagrams/${diagramId}`);
}

/**
 * Export the assets defined in a diagram into a TARA session.
 * Original call: POST /api/v1/diagrams/{id}/export-assets with body
 * `{ session_id, overwrite_existing? }`. POST + body, not GET.
 */
export async function exportDiagramAssets(diagramId, body) {
  return client.post(`/diagrams/${diagramId}/export-assets`, body);
}

/**
 * Original call: POST /api/v1/diagrams/${id} (with body `diagram`).
 * The original used POST (not PUT) for save-by-id; preserve.
 */
export async function saveDiagram(diagramId, diagram) {
  return client.post(`/diagrams/${diagramId}`, diagram);
}

/**
 * Original call: PUT /api/v1/diagrams/${id} (with body `diagram` or
 * autosave payload). Used by useAutoSave and ArchitectureDiagramListPage.
 */
export async function updateDiagram(diagramId, body) {
  return client.put(`/diagrams/${diagramId}`, body);
}

/**
 * Delete a diagram by ID. Original call: DELETE /api/v1/diagrams/${id}.
 */
export async function deleteDiagram(diagramId) {
  return client.delete(`/diagrams/${diagramId}`);
}

/**
 * Create a new diagram scoped to a session. Original call: POST
 * /api/v1/sessions/{sessionId}/diagrams with body `{ name }`.
 */
export async function createDiagramForSession(sessionId, body) {
  return client.post(
    `/sessions/${encodeURIComponent(sessionId)}/diagrams`,
    body
  );
}

// ── MBSE diagram intelligence ───────────────────────────────────────────────

/**
 * Validate an MBSE diagram against structural rules.
 * Mirrors AthenaBackendService.validateDiagram(diagramId).
 */
export async function validateDiagram(diagramId) {
  return client.post(`/diagrams/${diagramId}/validate`);
}

/**
 * Get auto-detected trust boundary crossings for a diagram.
 * Mirrors AthenaBackendService.getTrustBoundaries(diagramId).
 */
export async function getTrustBoundaries(diagramId) {
  return client.get(`/diagrams/${diagramId}/trust-boundaries`);
}

/**
 * Get the computed attack surface from a diagram's topology.
 * Mirrors AthenaBackendService.getAttackSurface(diagramId).
 */
export async function getAttackSurface(diagramId) {
  return client.get(`/diagrams/${diagramId}/attack-surface`);
}

/**
 * Get the STRIDE threat overlay for a diagram.
 * Mirrors AthenaBackendService.getThreatOverlay(diagramId).
 */
export async function getThreatOverlay(diagramId) {
  return client.get(`/diagrams/${diagramId}/threat-overlay`);
}

/**
 * Get the structured item-definition export for an MBSE diagram.
 * Mirrors AthenaBackendService.getItemDefinition(diagramId).
 *
 * Renamed from `getItemDefinition` to avoid colliding with
 * `workflow.getItemDefinition(threadId)` — both endpoints exist on the
 * backend but they live in different domains and accept different IDs.
 */
export async function getDiagramItemDefinition(diagramId) {
  return client.get(`/diagrams/${diagramId}/item-definition`);
}

/**
 * Sync diagram assets to a TARA session.
 * Mirrors AthenaBackendService.syncDiagramAssets(diagramId, options).
 */
export async function syncDiagramAssets(
  diagramId,
  { sessionId, direction = 'to_tara', overwriteExisting = false }
) {
  return client.post(`/diagrams/${diagramId}/sync-assets`, {
    session_id: sessionId,
    direction,
    overwrite_existing: overwriteExisting,
  });
}

// ── Catalogs + templates ────────────────────────────────────────────────────

/**
 * Get the technology catalog (component tags + STRIDE threat mappings).
 * Mirrors AthenaBackendService.getTechnologyCatalog().
 */
export async function getTechnologyCatalog() {
  return client.get('/diagrams/catalogs/technology');
}

/**
 * Get the channel-threat catalog (per-channel STRIDE profiles).
 * Mirrors AthenaBackendService.getChannelCatalog().
 */
export async function getChannelCatalog() {
  return client.get('/diagrams/catalogs/channels');
}

/**
 * List diagram templates, optionally filtered by category.
 * Mirrors AthenaBackendService.listDiagramTemplates(category).
 */
export async function listDiagramTemplates(category = null) {
  const path = '/diagrams/templates/list';
  if (!category) return client.get(path);
  return client.get(`${path}?category=${encodeURIComponent(category)}`);
}
